import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';
import { Platform, RefreshControl, Pressable, ScrollView, FlatList } from 'react-native';
import DefaultScreen from '@components/Global/DefaultScreenView';
import { DropDownPicker, StyledModal, Title, View, Text, Modal, LoadingModal } from '@constants/Themed';
import GenerateSchemaForm from '@hooks/generateSchemaForm';
import Button from '@components/UI/Button/Button';
import { SET_SALES_DATE_FILTER } from '@reduxLocal/actions/types';
import { themeSelector } from '@reduxLocal/selectors/themeSelector';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import apis from '@api/api';
import ActiveCampaignItem from './Components/ActiveCampaignItem';
import AnimatedLottieView from '@utils/lottieAnimation';
import { AntDesign } from '@expo/vector-icons';
import styles from '@stylesheet';
import { UserListType, UserType } from '../../../../types/userTypes';
import { H6, Spinner } from 'tamagui';
import { set } from 'lodash';
import CampaignStageBuilder from './Components/CampaignStageBuilder';
import { useDispatch } from 'react-redux';
// Lazy load TensorFlow to avoid startup issues
// import { load } from '@tensorflow-models/universal-sentence-encoder';
import useGlobalUser from '@hooks/getGlobalUser';
import LottieView from '@utils/lottieAnimation';
import ErrorBoundary from '@utils/ErrorBoundary';

import { useDeviceType } from '@hooks/useDeviceType';

const SalesDateFilter = () => {
  const dispatch = useDispatch();
  const [items, setItems] = useState([
    { label: 'Current', value: 'current' },
    { label: 'Today', value: 'today' },
    { label: 'Yesterday', value: 'yesterday' },
    { label: 'This Week', value: 'thisWeek' },
    { label: 'Last Week', value: 'lastWeek' },
    { label: 'This Month', value: 'thisMonth' },
    { label: 'Last Month', value: 'lastMonth' },
    { label: 'This Year', value: 'thisYear' },
    { label: 'Last Year', value: 'lastYear' },
    { label: 'Custom', value: 'custom' }
  ]);
  const [open, setOpen] = useState(false);
  const [value, setValue] = useState('month');

  const selectDateFilter = (item: { value: React.SetStateAction<string>; }) => {
    dispatch({ type: SET_SALES_DATE_FILTER, payload: item })
    setValue(item.value);
  }

  return (
    <DropDownPicker items={items} open={open} value={value} setOpen={setOpen} placeHolder={"Date Filter"} onSelectItem={selectDateFilter} />
  )
}

export interface StageSchema {
  title: string;
  description: string;
  owner: UserListType;
  stage_action: any;
}

const ActiveCampaigns = forwardRef((props, ref) => {
  const theme = themeSelector();
  const queryClient = useQueryClient();
  const modalRef = useRef(null);
  const [refreshing, setRefreshing] = useState(false);
  const builderRef = useRef(null);
  const loadingModalRef = useRef(null);
  const selectedId = useGlobalUser();
  const { isTablet } = useDeviceType();

  const { status, data: activeCampaigns, error, isFetching, isLoading, refetch } = useQuery({
    queryKey: ['activeCampaigns'],
    queryFn: async () => {
      const data = await apis.getActiveCampaigns()
        .then(res => {
          return res
        });
      return data
    },
  })

  const onRefresh = useCallback(async () => {
    setRefreshing(true);
    await refetch()
      .then(() =>
        setRefreshing(false));
  }, []);

  const _renderItem = ({ item, index }) => {
    return (
      <ErrorBoundary>
        <ActiveCampaignItem item={item} index={index} />
      </ErrorBoundary>
    )
  }

  const schema = {
    // TEXT TYPE
    campaign_title: { validation: { required: true }, type: "text", helpText: "Select a title for your campaign " },
    // DATE TYPE
    start_date: { validation: { required: true }, type: "date", helpText: "Select a start date for your campaign ", ref: "date" },
    end_date: { validation: { required: true }, type: "date", helpText: "Select an end date for your campaign ", ref: "date" },
    target_audience: { type: "text", helpText: "Select target audience dataset", ref: "customer_datas" },
    marketing_scope: { validation: { required: true, enum: ['email campaign', 'website campaign', 'social media campaign', 'all the above'] }, helpText: 'Select the scope of your campaign', type: 'dropDown' },
    goals: { type: "number", helpText: "Establish your campaign goals", ref: "multiline" },
    description:{ type: "text", helpText: "Enter a description for your campaign"},
    //MULTILINE TEXT TYPE
    // creative_concept: { type: "text", helpText: "Establish your creative concept", ref: "multiline" },
    // //NUMBER TYPE
    // budget: { type: 'currency', helpText: 'Enter a budget for your campaign' },
    // //ENSURE THAT THIS IS BULLSHIT 
    // financial_projection: { type: 'currency', helpText: 'Select a financial projection for your campaign' },
    sales_target: { type: 'number', helpText: 'Determine your sales target for your campaign', ref: 'number' },
    // DROPDOWN TYPE, ENUM NEDDS TO HAVE [] AND LISTED THINGS INSIDE WITH ,
    status: { validation: { required: true, enum: ['active', 'inactive', 'completed', 'draft', 'pending'] }, helpText: 'Select the status of your campaign', type: 'dropDown' },
  }


  return (
    <DefaultScreen backButton containerStyle={{ paddingRight: 0 }} style={{ paddingRight: 0}} scrollDisabled searchBar={
      {
        backButton: Platform.OS != 'web' ? true : false,
        placeHolder: "Search Campaigns",
        customIcons: [
          {
            functionName: 'openAddCampign',
            icon: <AntDesign name="folder-add" size={14} color="white" />,
            function: () => ref.current?.openModal?.(),
          }
        ]
      }
    }>
      <View style={{ flex: 1 }}>
        {
          isLoading ? (
            <Spinner />
          ) : (
              <FlatList
              showsVerticalScrollIndicator={false}
                contentContainerStyle={{ paddingBottom: Platform.select({ web: 100, default: 150 }) }}
                ListEmptyComponent={() => (<View style={{ paddingVertical: Platform.select({ web: "25%", default: isTablet ? "20%" : "60%" }), gap: 10, flex: 1 }}>
                  <LottieView source={require("@assets/animations/40276-man-reviewing-business-analytics.json")} autoPlay loop style={[styles.centerEverything, { flex: 1, alignSelf: 'center', justifyContent: 'center', height: 150, width: 150, left: 10, alignContent: 'center', alignItems: 'center' }]} />
                  <H6 style={[styles.centerEverything, { textAlign: "center" }]}>{"No Active Campaigns just yet"}</H6>
                  <Text style={{ textAlign: "center", flexWrap: "wrap" }}>Try creating a campaign, make sure to have some users and templates in your data first.</Text>
                </View>)}
                data={activeCampaigns}
                renderItem={_renderItem}
                refreshControl={< RefreshControl refreshing={refreshing} onRefresh={() => { onRefresh() }} />}
              />
          )
        }
      </View>
    </DefaultScreen >
  )
});
export default ActiveCampaigns;